import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ProductModelFile } from '@app/api/Products';
import {
  useAddEmployeePortfolioMutation,
  useDeleteEmployeePortfolioMutation,
  useGetEmployeePortfolioQuery,
  useUpdateEmployeePortfolioMutation,
} from '@app/api/User/api';
import { FRONT_WARNING, SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NUser } from '@app/services/rtkQuery/User/types';

import type {
  PortfolioItem,
  AddPortfolioValues,
  EditPortfolioValues,
} from '@pages/Portfolio/types';

export const usePortfolio = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const employeeId = useMemo(() => {
    const idValue = searchParams.get('id');
    if (!idValue) {
      return 0;
    }
    const parsed = Number(idValue);
    if (Number.isNaN(parsed)) {
      return 0;
    }
    return parsed;
  }, [searchParams]);

  const isAddModalOpen = useMemo(
    () => searchParams.get('portfolioAction') === 'add',
    [searchParams]
  );

  const [portfolios, setPortfolios] = useState<PortfolioItem[]>([]);
  const [editingItem, setEditingItem] = useState<PortfolioItem | null>(null);
  const [deletingItem, setDeletingItem] = useState<PortfolioItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const {
    data,
    isLoading: isPortfolioLoading,
    isFetching: isPortfolioFetching,
    refetch,
  } = useGetEmployeePortfolioQuery({
    ActiveBranchId: activeBranchId,
    employeeId,
  });

  const [addEmployeePortfolio, { isLoading: isAddLoading }] =
    useAddEmployeePortfolioMutation();
  const [updateEmployeePortfolio, { isLoading: isUpdateLoading }] =
    useUpdateEmployeePortfolioMutation();
  const [deleteEmployeePortfolio, { isLoading: isDeleteLoading }] =
    useDeleteEmployeePortfolioMutation();

  useEffect(() => {
    if (data?.Portfolios) {
      const sorted = [...data.Portfolios].sort(
        (first, second) => first.Order - second.Order
      );
      setPortfolios(sorted);
    } else {
      setPortfolios([]);
    }
  }, [data]);

  const closeAddModal = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('portfolioAction');
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const openEditModal = useCallback((item: PortfolioItem) => {
    setEditingItem(item);
    setIsEditModalOpen(true);
  }, []);

  const closeEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingItem(null);
  }, []);

  const openDeleteModal = useCallback((item: PortfolioItem) => {
    setDeletingItem(item);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeletingItem(null);
  }, []);

  const startDragging = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  const readFileAsBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result === 'string') {
          const parts = reader.result.split(',');
          const base64 = parts.length > 1 ? parts[1] : parts[0];
          resolve(base64);
        } else {
          reject(new Error('Не удалось прочитать файл'));
        }
      };

      reader.onerror = () => {
        if (reader.error) {
          reject(reader.error);
        } else {
          reject(new Error('Не удалось прочитать файл'));
        }
      };

      reader.readAsDataURL(file);
    });
  }, []);

  const handleAddSubmit = useCallback(
    async (values: AddPortfolioValues) => {
      if (!employeeId || !activeBranchId) {
        showNotification(
          'Не удалось определить исполнителя для сохранения портфолио',
          FRONT_WARNING
        );
        return;
      }

      if (!values.files.length) {
        showNotification(
          'Выберите хотя бы один файл для загрузки',
          FRONT_WARNING
        );
        return;
      }

      try {
        const filesItems: NUser.ModelEmployeePortfolioFileItem[] = [];

        for (const file of values.files) {
          const base64 = await readFileAsBase64(file);
          const fileModel: ProductModelFile = {
            Id: 0,
            Name: file.name,
            Data: base64,
            Description: values.description,
            Lifetime: null,
          };

          filesItems.push({
            Description: values.description,
            File: fileModel,
          });
        }

        await addEmployeePortfolio({
          ActiveBranchId: activeBranchId,
          data: {
            UserId: employeeId,
            Description: values.description,
            Files: filesItems,
          },
        }).unwrap();

        closeAddModal();
        showNotification('Изображения добавлены в портфолио', SUCCESS);
        refetch();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      activeBranchId,
      addEmployeePortfolio,
      closeAddModal,
      employeeId,
      readFileAsBase64,
      refetch,
      showNotification,
    ]
  );

  const handleEditSubmit = useCallback(
    async (values: EditPortfolioValues) => {
      if (!editingItem) {
        return;
      }

      if (!activeBranchId) {
        showNotification(
          'Не удалось определить филиал для сохранения портфолио',
          FRONT_WARNING
        );
        return;
      }

      try {
        await updateEmployeePortfolio({
          ActiveBranchId: activeBranchId,
          data: {
            Id: editingItem.Id,
            UserId: editingItem.UserId,
            Description: values.description,
            Order: editingItem.Order,
            File: null,
          },
        }).unwrap();

        closeEditModal();
        showNotification('Изменения сохранены', SUCCESS);

        refetch();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      activeBranchId,
      closeEditModal,
      editingItem,
      refetch,
      showNotification,
      updateEmployeePortfolio,
    ]
  );

  const handleDeleteSubmit = useCallback(async () => {
    if (!deletingItem || !employeeId || !activeBranchId) {
      return;
    }

    try {
      await deleteEmployeePortfolio({
        ActiveBranchId: activeBranchId,
        data: {
          UserId: employeeId,
          PortfolioIds: [deletingItem.Id],
        },
      }).unwrap();

      closeDeleteModal();
      showNotification('Изображение удалено из портфолио', SUCCESS);

      refetch();
    } catch (error) {
      showNotification(error);
    }
  }, [
    activeBranchId,
    closeDeleteModal,
    deleteEmployeePortfolio,
    deletingItem,
    employeeId,
    refetch,
    showNotification,
  ]);

  const handleDropOnIndex = useCallback(
    async (targetIndex: number) => {
      if (draggedIndex === null || draggedIndex === targetIndex) {
        setDraggedIndex(null);
        return;
      }

      if (!activeBranchId || !employeeId) {
        setDraggedIndex(null);
        showNotification(
          'Не удалось изменить порядок элементов портфолио',
          FRONT_WARNING
        );
        return;
      }

      const movedItem = portfolios[draggedIndex];

      if (!movedItem) {
        setDraggedIndex(null);
        return;
      }

      setPortfolios((previous) => {
        const updated = [...previous];
        const [removed] = updated.splice(draggedIndex, 1);
        updated.splice(targetIndex, 0, removed);
        return updated;
      });

      setDraggedIndex(null);

      try {
        await updateEmployeePortfolio({
          ActiveBranchId: activeBranchId,
          data: {
            Id: movedItem.Id,
            UserId: movedItem.UserId,
            Description: movedItem.Description,
            Order: targetIndex,
            File: null,
          },
        }).unwrap();

        refetch();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      activeBranchId,
      draggedIndex,
      employeeId,
      portfolios,
      refetch,
      showNotification,
      updateEmployeePortfolio,
    ]
  );

  const handleDownload = useCallback(
    (item: PortfolioItem) => {
      const file = item.File;

      if (!file || !file.Data) {
        showNotification('Файл недоступен для скачивания', FRONT_WARNING);
        return;
      }

      try {
        const binaryString = window.atob(file.Data);
        const { length } = binaryString;
        const bytes = new Uint8Array(length);

        for (let index = 0; index < length; index += 1) {
          bytes[index] = binaryString.charCodeAt(index);
        }

        const blob = new Blob([bytes], {
          type: 'application/octet-stream',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = file.Name ?? 'file';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        showNotification(
          'Произошла ошибка при скачивании файла',
          FRONT_WARNING
        );
      }
    },
    [showNotification]
  );

  return {
    portfolios,
    isAddModalOpen,
    isEditModalOpen,
    isDeleteModalOpen,
    isAddLoading,
    isUpdateLoading,
    isDeleteLoading,
    editingItem,
    deletingItem,
    startDragging,
    handleDropOnIndex,
    openEditModal,
    closeEditModal,
    openDeleteModal,
    closeDeleteModal,
    closeAddModal,
    handleDownload,
    handleAddSubmit,
    handleEditSubmit,
    handleDeleteSubmit,
    isPortfolioLoading,
    isPortfolioFetching,
  };
};
